import { Show, createSignal } from "solid-js";
import { CourtCard } from "../components/dev/CourtCard";
import { QueueList } from "../components/dev/QueueList";
import { EventLog } from "../components/dev/EventLog";
import { type AuthState, clearAuth, loadAuth, saveAuth } from "../lib/auth";
import { login } from "../lib/login";
import {
    cancelQueueEntry,
    checkIn,
    checkout,
    denounce,
    extend,
    joinQueue,
    passQueueEntry,
} from "../api-extras";
import { useEvents } from "../lib/sse/use-events";
import { Avatar, Button, Card, Input } from "../components/ui";
import type { CourtSummary } from "@saquei/types";

export function DevPanel() {
    const [auth, setAuth] = createSignal<AuthState | null>(loadAuth());
    const [email, setEmail] = createSignal("");
    const [password, setPassword] = createSignal("");
    const [loginError, setLoginError] = createSignal<string | null>(null);
    const [submitting, setSubmitting] = createSignal(false);

    async function handleLogin(e: Event) {
        e.preventDefault();
        setLoginError(null);
        setSubmitting(true);
        try {
            const resp = await login(email(), password());
            const role = resp.roles[0] ?? "member";
            const a = { token: resp.access_token, memberId: resp.member_id, role };
            saveAuth(a);
            setAuth(a);
        } catch (err: any) {
            setLoginError(err?.message ?? "login falhou");
        } finally {
            setSubmitting(false);
        }
    }

    function handleLogout() {
        clearAuth();
        setAuth(null);
    }

    return (
        <main class="dev-shell">
            <header class="dev-shell-head">
                <div class="brand">
                    <span class="brand-glyph" aria-hidden="true" />
                    <span class="brand-mark">saquei</span>
                    <span class="brand-tag">dev</span>
                </div>
                <span class="dev-env">não-produção</span>
            </header>

            <Show
                when={auth()}
                fallback={
                    <Card variant="raised" class="dev-login-card">
                        <h1 class="dev-login-title">Entrar</h1>
                        <p class="dev-login-sub">Painel de desenvolvimento — sindico ou admin.</p>
                        <form onSubmit={handleLogin} class="dev-login-form">
                            <Input
                                label="email"
                                type="email"
                                autocomplete="email"
                                value={email()}
                                onInput={(e) => setEmail(e.currentTarget.value)}
                                fullWidth
                                required
                            />
                            <Input
                                label="senha"
                                type="password"
                                autocomplete="current-password"
                                value={password()}
                                onInput={(e) => setPassword(e.currentTarget.value)}
                                fullWidth
                                required
                                error={loginError() ?? undefined}
                            />
                            <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting()}>
                                Entrar
                            </Button>
                        </form>
                    </Card>
                }
            >
                {(currentAuth) => <DevAuthed auth={currentAuth} onLogout={handleLogout} />}
            </Show>
        </main>
    );
}

interface DevAuthedProps {
    auth: () => AuthState;
    onLogout: () => void;
}

function DevAuthed(props: DevAuthedProps) {
    const tenant = window.location.hostname.split(".")[0] ?? "demo";

    const eventsState = useEvents(tenant, {
        mode: "authenticated",
        token: props.auth().token,
        onTokenRevoked: props.onLogout,
    });

    const courts = (): CourtSummary[] =>
        Object.values(eventsState().courts).sort((a, b) =>
            a.short_code.localeCompare(b.short_code, "pt-BR", { numeric: true }),
        );
    const queue = () => eventsState().queue;
    const recentEvents = () => eventsState().recentEvents;

    async function onCheckIn(court: CourtSummary) {
        const a = props.auth();
        try {
            await checkIn(a.token, court.short_code, {
                party: [{ member_id: a.memberId, name: "Player", apartment: null, is_guest: false }],
            });
        } catch (err: any) {
            alert(`Check-in falhou: ${err?.message ?? "?"}`);
        }
    }

    async function onCheckOut(court: CourtSummary) {
        const a = props.auth();
        if (!court.active_session_id) return;
        try {
            await checkout(a.token, court.active_session_id);
        } catch (err: any) {
            alert(`Check-out falhou: ${err?.message ?? "?"}`);
        }
    }

    async function onExtend(court: CourtSummary) {
        const a = props.auth();
        if (!court.active_session_id) return;
        try {
            await extend(a.token, court.active_session_id);
        } catch (err: any) {
            alert(`Estender falhou: ${err?.message ?? "?"}`);
        }
    }

    async function onDenounce(court: CourtSummary) {
        const a = props.auth();
        try {
            await denounce(a.token, court.short_code);
        } catch (err: any) {
            alert(`Denuncia falhou: ${err?.message ?? "?"}`);
        }
    }

    async function onJoinSolo() {
        const a = props.auth();
        try {
            await joinQueue(a.token, {
                preferred_court_id: null,
                party: [{ member_id: a.memberId, name: "Player", apartment: null, is_guest: false }],
            });
        } catch (err: any) {
            alert(`Entrar fila falhou: ${err?.message ?? "?"}`);
        }
    }

    async function onCancelQueue(id: string) {
        const a = props.auth();
        try {
            await cancelQueueEntry(a.token, id);
        } catch (err: any) {
            alert(`Cancelar falhou: ${err?.message ?? "?"}`);
        }
    }

    async function onPassQueue(id: string) {
        const a = props.auth();
        try {
            await passQueueEntry(a.token, id);
        } catch (err: any) {
            alert(`Passar falhou: ${err?.message ?? "?"}`);
        }
    }

    return (
        <div class="dev-authed">
            <header class="dev-authed-head">
                <div class="dev-user">
                    <Avatar name={props.auth().role.toUpperCase()} size="md" />
                    <div class="dev-user-info">
                        <strong>{props.auth().role}</strong>
                        <code>{props.auth().memberId}</code>
                    </div>
                </div>
                <Button variant="ghost" size="sm" onClick={props.onLogout}>
                    Sair
                </Button>
            </header>

            <section class="dev-section">
                <h2>Quadras ({courts().length})</h2>
                <div class="dev-courts-grid">
                    {courts().map((court) => (
                        <CourtCard
                            court={court}
                            onCheckIn={() => onCheckIn(court)}
                            onCheckOut={() => onCheckOut(court)}
                            onExtend={() => onExtend(court)}
                            onDenounce={() => onDenounce(court)}
                        />
                    ))}
                </div>
            </section>

            <section class="dev-section">
                <QueueList
                    items={queue()}
                    onJoinSolo={onJoinSolo}
                    onCancel={onCancelQueue}
                    onPass={onPassQueue}
                />
            </section>

            <section class="dev-section">
                <EventLog events={recentEvents()} />
            </section>
        </div>
    );
}
